/**
 * Mise en scène du reveal — Souffle Diplomatique.
 *
 * Reçoit les payloads `cardResolved` (émis par le serveur carte par carte,
 * à rythme adaptatif) et théâtralise :
 *
 * - Cartes banales (extension/densité/défense/intel sans conséquence) :
 *   un son court, le bandeau compact existant fait le reste. Rafale.
 * - Cartes dramatiques (risque, sabotage, chargement, exposition) :
 *   CÉRÉMONIE — overlay plein écran avec la carte en grand (illustration,
 *   flavor), roulement de tambour sur les jets de risque, tampon de verdict
 *   (RÉUSSI/RATÉ), gong et bandeau rouge quand quelqu'un passe Exposé.
 *
 * Le serveur attend 3,6 s après une carte dramatique (0,9 s sinon) : la
 * timeline ici est calée pour tenir dans cette fenêtre.
 */

import { sounds, playFartForCard, playCardRevealSound } from "./sound";

export interface CardResolvedPayload {
  idx: number;
  total: number;
  playerId: string;
  playerName: string;
  cardId: string;
  instanceId: string;
  cardName: string;
  category: string;
  flavor: string;
  targetPlayerId?: string;
  targetPlayerName?: string;
  riskFailed: boolean | null;
  charged: boolean;
  newlyExposed: Array<{ id: string; name: string }>;
  newlyCleared: Array<{ id: string; name: string }>;
  newlyVassalized: Array<{ id: string; name: string }>;
  lines: string[];
  dramatic: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  extension: "Extension",
  density: "Densité",
  risk: "Risque",
  defense: "Défense",
  sabotage: "Sabotage",
  intel: "Renseignement",
  signature: "Signature",
};

let overlayEl: HTMLElement | null = null;
let ceremonyTimers: number[] = [];

function clearCeremonyTimers() {
  for (const t of ceremonyTimers) window.clearTimeout(t);
  ceremonyTimers = [];
  sounds.stop("roulement_tambour");
}

function later(fn: () => void, ms: number) {
  ceremonyTimers.push(window.setTimeout(fn, ms));
}

function ensureOverlay(): HTMLElement {
  if (overlayEl && document.body.contains(overlayEl)) return overlayEl;
  const el = document.createElement("div");
  el.id = "ceremony-overlay";
  el.className = "ceremony-overlay";
  el.hidden = true;
  el.innerHTML = `
    <div class="ceremony-backdrop"></div>
    <div class="ceremony-panel">
      <div class="ceremony-player" id="ceremony-player">—</div>
      <div class="ceremony-card" id="ceremony-card">
        <div class="ceremony-illu-wrap" id="ceremony-illu-wrap"></div>
        <div class="ceremony-cat" id="ceremony-cat">—</div>
        <div class="ceremony-name" id="ceremony-name">—</div>
        <div class="ceremony-flavor" id="ceremony-flavor"></div>
        <div class="ceremony-stamp" id="ceremony-stamp" hidden>RATÉ</div>
      </div>
      <div class="ceremony-outcome" id="ceremony-outcome" hidden></div>
    </div>`;
  document.body.appendChild(el);
  overlayEl = el;
  return el;
}

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

function setIllustration(cardId: string) {
  const wrap = document.getElementById("ceremony-illu-wrap");
  if (!wrap) return;
  wrap.innerHTML = "";
  const img = document.createElement("img");
  img.className = "ceremony-illu";
  img.alt = "";
  const exts = ["png", "jpg", "webp"];
  let i = 0;
  img.onerror = () => {
    i++;
    if (i < exts.length) img.src = `/illustrations/${cardId}.${exts[i]}`;
    else img.remove();
  };
  img.src = `/illustrations/${cardId}.${exts[0]}`;
  wrap.appendChild(img);
}

function hideCeremony() {
  const el = ensureOverlay();
  el.classList.remove("ceremony-show");
  later(() => {
    if (!el.classList.contains("ceremony-show")) el.hidden = true;
  }, 280);
}

/**
 * Point d'entrée : théâtralise une résolution de carte.
 * Retourne la durée d'affichage prévue (pour synchroniser le bandeau).
 */
export function stageCardResolution(
  p: CardResolvedPayload,
  opts: { playerColor?: string; isMe?: boolean } = {}
): number {
  // Son de reveal des cartes banales — puis le bandeau compact suffit.
  if (!p.dramatic) {
    playCardRevealSound(p);
    return 850;
  }

  // ── CÉRÉMONIE ──
  clearCeremonyTimers();
  const el = ensureOverlay();
  const $ = (id: string) => document.getElementById(id)!;

  // Couleur du joueur (identification immédiate de qui joue)
  const panel = el.querySelector<HTMLElement>(".ceremony-panel");
  panel?.style.setProperty("--pcolor", opts.playerColor ?? "#8b8499");
  const vous = opts.isMe ? " (vous)" : "";
  $("ceremony-player").innerHTML =
    `<span class="ceremony-player-dot" aria-hidden="true"></span><strong>${esc(p.playerName)}${vous}</strong> joue`;
  $("ceremony-cat").textContent = CATEGORY_LABELS[p.category] ?? p.category;
  $("ceremony-cat").className = `ceremony-cat cat-${p.category}`;
  $("ceremony-name").textContent = p.cardName;
  $("ceremony-flavor").textContent = p.flavor ? `« ${p.flavor} »` : "";
  const stamp = $("ceremony-stamp");
  stamp.hidden = true;
  stamp.className = "ceremony-stamp";
  const outcome = $("ceremony-outcome");
  outcome.hidden = true;
  outcome.className = "ceremony-outcome";
  const card = $("ceremony-card");
  card.classList.remove("ceremony-card-shake");
  setIllustration(p.cardId);

  el.hidden = false;
  // reflow pour déclencher la transition d'entrée
  void el.offsetWidth;
  el.classList.add("ceremony-show");
  sounds.play("carte_reveal");

  const finishAt = 3300; // le serveur enchaîne à 3600

  const announceExposed = (delay: number) => {
    if (p.newlyExposed.length === 0) return;
    // RETOUR DE FLAMME : le lanceur s'expose LUI-MÊME (backfire d'accusation
    // à vide, échec de pari...). Le moment le plus drôle du jeu — il doit
    // être impossible à rater : tampon FIASCO + message dédié + womp-womp.
    const selfExposed = p.newlyExposed.some((e) => e.id === p.playerId);
    later(() => {
      sounds.play("gong_exposition");
      card.classList.add("ceremony-card-shake");
      if (selfExposed) {
        sounds.play("verdict_echec");
        if (p.riskFailed === null && stamp.hidden) {
          // Pas de jet affiché (backfire direct) : le tampon porte le fiasco
          stamp.textContent = "FIASCO";
          stamp.classList.add("ceremony-stamp-fail");
          stamp.hidden = false;
        }
        const others = p.newlyExposed.filter((e) => e.id !== p.playerId).map((e) => e.name);
        outcome.innerHTML =
          `💥 <strong>Retour de flamme !</strong> ${esc(p.playerName)} s'expose tout seul` +
          (others.length > 0 ? ` (et ${esc(others.join(", "))} aussi)` : "") + ` !`;
      } else {
        const names = p.newlyExposed.map((e) => e.name).join(", ");
        outcome.innerHTML = `🚨 <strong>${esc(names)}</strong> ${p.newlyExposed.length > 1 ? "sont EXPOSÉS" : "est EXPOSÉ"} !`;
      }
      outcome.classList.add("ceremony-outcome-exposed");
      outcome.hidden = false;
    }, delay);
  };

  if (p.riskFailed !== null) {
    // Jet de risque : suspense → verdict → conséquences
    later(() => sounds.play("roulement_tambour"), 250);
    later(() => {
      sounds.stop("roulement_tambour");
      if (p.riskFailed) {
        sounds.play("verdict_echec");
        stamp.textContent = "RATÉ";
        stamp.classList.add("ceremony-stamp-fail");
      } else {
        sounds.play("verdict_reussite");
        stamp.textContent = "RÉUSSI";
        stamp.classList.add("ceremony-stamp-success");
        // La réussite d'un risque, c'est un pet qui passe crème
        later(() => playFartForCard(p.cardId, p.category, p.instanceId), 220);
      }
      stamp.hidden = false;
    }, 1750);
    announceExposed(2450);
  } else {
    // Dramatique sans jet : sabotage, chargement, exposition directe
    later(() => playCardRevealSound(p), 300);
    if (p.charged) {
      later(() => {
        outcome.innerHTML = `⏳ La carte est posée <strong>en chargement</strong> — quelque chose couve…`;
        outcome.classList.add("ceremony-outcome-charged");
        outcome.hidden = false;
      }, 900);
    }
    announceExposed(1250);
    if (p.newlyVassalized.length > 0) {
      later(() => {
        const names = p.newlyVassalized.map((e) => e.name).join(", ");
        outcome.innerHTML = `⛓ <strong>${esc(names)}</strong> est vassalisé !`;
        outcome.hidden = false;
      }, 1400);
    }
  }

  later(hideCeremony, finishAt);
  return finishAt;
}

/** Coupe toute cérémonie en cours (changement de phase, fin de partie...). */
export function abortCeremony() {
  clearCeremonyTimers();
  const el = overlayEl;
  if (el) {
    el.classList.remove("ceremony-show");
    el.hidden = true;
  }
}

// ============================================================ //
// Dépêches satiriques — bandeau "flash presse" éphémère         //
// ============================================================ //

let dispatchTimer: number | null = null;

export function showDispatch(headline: string, tone: string) {
  let strip = document.getElementById("dispatch-strip");
  if (!strip) {
    strip = document.createElement("div");
    strip.id = "dispatch-strip";
    strip.className = "dispatch-strip";
    document.body.appendChild(strip);
  }
  strip.innerHTML = `<span class="dispatch-tag">DÉPÊCHE</span> <span class="dispatch-text">${esc(headline)}</span>`;
  strip.dataset.tone = tone;
  strip.classList.add("dispatch-show");
  if (dispatchTimer != null) window.clearTimeout(dispatchTimer);
  dispatchTimer = window.setTimeout(() => {
    strip!.classList.remove("dispatch-show");
  }, 5200);
}
